import type { ChangeEvent } from 'react'
import { useRef, useState, useEffect, useCallback } from 'react'

import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass'
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import CustomButton from '@space-core/components/customButton'
import Icon from '@space-core/components/icon'
import { Actions, Entities } from '@space-core/datahub'
import { useMediaQuery } from '@space-core/hooks'
import { ScreenSizes } from '@space-core/styles'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'

import { useTranslation } from 'react-i18next'

import InputField from './inputField'

import { MIN_QUERY_VALUE } from '../constants'
import SearchField from '../searchField'
import { CloseButton, Label } from '../styles'
import type { SearchBoxProps } from '../types'

const SearchBox = ({ isLoading, onSubmit, onChange, term }: SearchBoxProps) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.md})`)
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [showInput, setShowInput] = useState<boolean>(false)
  const [showButtonSearch, setShowButtonSearch] = useState<boolean>(false)
  const { sendDataHubEvent } = useDataHubEvents()
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()

  const handleSubmit = (event: React.FormEvent | React.MouseEvent) => {
    setShowButtonSearch(false)
    inputRef.current?.focus()
    inputRef.current?.blur()

    onSubmit(event)
  }

  const handleCloseSearch = useCallback(() => {
    inputRef.current?.focus()
    inputRef.current?.blur()

    sendDataHubEvent({
      group: Entities.HEADER_SEARCH,
      name: Actions.CLICK_EXIT_SEARCH,
      params: {
        search: {
          term
        }
      }
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER_SEARCH,
      name: Actions.CLICK_EXIT_SEARCH,
      label: term
    })
  }, [sendDataHubEvent, sendGoogleAnalyticsEvent, term])

  const handleActiveSearch = useCallback(() => {
    setShowInput(true)
    setShowButtonSearch(term.length > MIN_QUERY_VALUE)

    sendDataHubEvent({
      group: Entities.HEADER_SEARCH,
      name: Actions.CLICK_ACTIVATE_SEARCH
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER_SEARCH,
      name: Actions.CLICK_ACTIVATE_SEARCH,
      label: term
    })
  }, [sendDataHubEvent, sendGoogleAnalyticsEvent, term])

  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      if (searchRef.current && !searchRef.current?.contains(e.target as HTMLDivElement)) {
        setShowInput(false)

        setShowButtonSearch(false)
      }
    }

    document.addEventListener('click', handleClickOutside, true)

    return () => document.removeEventListener('click', handleClickOutside, true)
  }, [])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value.toLowerCase())
    setShowButtonSearch(e.target.value.length > MIN_QUERY_VALUE)
  }

  return (
    <SearchField isMobile={isMobile} isLoading={isLoading} ref={searchRef}>
      {isMobile && (
        <CloseButton
          onClick={handleCloseSearch}
          data-test='button-search-close'
          className='_close-search _align-items-center _border-0 _text-5 _text-gray-900 _line-height-sm'>
          <Icon icon={faXmark} />
        </CloseButton>
      )}

      <form className='_w-full' onSubmit={handleSubmit}>
        {showInput && (
          <InputField
            placeholder={t('search.placeholder')}
            ref={inputRef}
            value={term}
            onChange={handleChange}
          />
        )}
      </form>

      <Label htmlFor='search' data-test='label-search'>
        {!showButtonSearch ? (
          <div className='_mr-4 _mt-1'>
            <Icon
              onClick={handleActiveSearch}
              className='_text-gray-900'
              data-test='icon-search'
              icon={faMagnifyingGlass}
            />
          </div>
        ) : (
          <CustomButton
            className='_search-button'
            slot='actions-end'
            type='submit'
            size='sm'
            loading={isLoading}
            onClick={handleSubmit}
            data-test='button-search'>
            {t('search.button')}
          </CustomButton>
        )}
      </Label>
    </SearchField>
  )
}

export default SearchBox
